// Updated ROIComponents.jsx with combined authorization section

import React, { useState, useEffect } from 'react';
import API from '../services/api';
import { todayEastern } from '../dateUtils';

// Default email body shown to the client (therapist can edit it completely before sending)
const DEFAULT_ROI_EMAIL_MESSAGE =
  'Please complete your Release of Information form by clicking the button below.';

// client_name is stored "Last, First" — derive the friendly first name and a "First Last" display.
function parseClientName(name) {
  const raw = (name || '').trim();
  if (!raw) return { firstName: '', fullName: '' };
  if (raw.includes(',')) {
    const [last, first] = raw.split(',').map(s => s.trim());
    return {
      firstName: (first || '').split(/\s+/)[0] || '',
      fullName: [first, last].filter(Boolean).join(' ').trim(),
    };
  }
  // Fallback: already "First Last"
  const parts = raw.split(/\s+/);
  return { firstName: parts[0] || '', fullName: raw };
}

// Modal for generating ROI form link with pre-filled data
export function GenerateROIModal({ isOpen, onClose, clientId, clientName, clientEmail }) {
  const { firstName: clientFirstName, fullName: clientFullName } = parseClientName(clientName);
  const [preFilledData, setPreFilledData] = useState({
    // Release to information
    authorizeReleaseTo: '',
    releaseToRelationship: '',
    releaseToPhone: '',
    releaseToEmail: '',
    
    // COMBINED: Information to be shared/released - all under one section
    shareProgressWithProvider: false,
    coordinateCareWithPsychiatrist: false,
    shareWithSchool: false,
    speakWithFamily: false,
    billUsingOtherCard: false,
    provideDocumentationToProvider: false,
    allowEmergencyContactInform: false,
    schedulingInfoOnly: false,
    treatmentProgress: false,
    diagnosisAndPlan: false,
    billingAndPayment: false,
    fullClinicalRecords: false,
    releaseOtherSpecify: '',
    
    // Purpose of disclosure
    purposeCoordinationCare: false,
    purposeBilling: false,
    purposeEmergency: false,
    purposeSupportFromFamily: false,
    purposeOtherSpecify: '',
    
    // Release duration
    durationOneYear: false,
    durationEndOfTreatment: false,
    durationSpecificDate: false,
    specificExpirationDate: '',
    
    // EAP section - Only show for EAP clients
    isEAPClient: false,
    eapAuthorizeSharing: false,
    eapUnderstandNoDetails: false,
    eapEmployer: '',
    eapUnderstandRefusalImpact: false,
    
    allowClientEdits: true // Control whether client can edit the form
  });

  const [generatedLink, setGeneratedLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);

  // Email compose state
  const [emailTo, setEmailTo] = useState('');
  const [greetingName, setGreetingName] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState('');

  // Set default expiration date to 1 year from now
  useEffect(() => {
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
    oneYearFromNow.setMinutes(oneYearFromNow.getMinutes() - oneYearFromNow.getTimezoneOffset());
    setPreFilledData(prev => ({
      ...prev,
      specificExpirationDate: oneYearFromNow.toISOString().split('T')[0]
    }));
  }, []);

  const handleInputChange = (field, value) => {
    setPreFilledData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // EAP Quick Fill Function
  const fillEAPData = () => {
    setPreFilledData(prev => ({
      ...prev,
      isEAPClient: true,
      authorizeReleaseTo: 'Blue Dog Bakery & Cafe',
      releaseToRelationship: 'Employer',
      releaseToPhone: '(502) 899-9800',
      releaseToEmail: '',
      eapEmployer: 'Blue Dog Bakery & Cafe',
      eapAuthorizeSharing: true,
      eapUnderstandNoDetails: true,
      eapUnderstandRefusalImpact: true,
      purposeBilling: true,
      durationEndOfTreatment: true,
      billingAndPayment: true,
      releaseOtherSpecify: 'Session dates and billing information. No clinical or other personal data will be shared with employer.'
    }));
  };

  // Primary Care Provider Quick Fill
  const fillPrimaryCareData = () => {
    setPreFilledData(prev => ({
      ...prev,
      shareProgressWithProvider: true,
      coordinateCareWithPsychiatrist: true,
      treatmentProgress: true,
      diagnosisAndPlan: true,
      purposeCoordinationCare: true,
      durationOneYear: true
    }));
  };

  // School Quick Fill
  const fillSchoolData = () => {
    setPreFilledData(prev => ({
      ...prev,
      shareWithSchool: true,
      treatmentProgress: true,
      purposeCoordinationCare: true,
      durationEndOfTreatment: true
    }));
  };

  const generateROILink = async () => {
    if (!clientId) {
      setError('No client selected');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // Calculate expiration date (7 days from now for the token)
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + 7);

      const response = await API.post('/forms/roi/generate', {
        clientId,
        expiresAt: expirationDate.toISOString(),
        preFilledData
      });

      if (response.data.success) {
        setGeneratedLink(response.data.link);
        setEmailTo(clientEmail || '');
        setGreetingName(clientFirstName);
        setEmailMessage(DEFAULT_ROI_EMAIL_MESSAGE);
        setEmailStatus('');
      } else {
        setError(response.data.error || 'Failed to generate link');
      }
    } catch (err) {
      console.error('Error generating ROI link:', err);
      setError('Server error generating link');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (!generatedLink) return;
    
    navigator.clipboard.writeText(generatedLink)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 3000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  const resetForm = () => {
    setPreFilledData({
      authorizeReleaseTo: '',
      releaseToRelationship: '',
      releaseToPhone: '',
      releaseToEmail: '',
      shareProgressWithProvider: false,
      coordinateCareWithPsychiatrist: false,
      shareWithSchool: false,
      speakWithFamily: false,
      billUsingOtherCard: false,
      provideDocumentationToProvider: false,
      allowEmergencyContactInform: false,
      schedulingInfoOnly: false,
      treatmentProgress: false,
      diagnosisAndPlan: false,
      billingAndPayment: false,
      fullClinicalRecords: false,
      releaseOtherSpecify: '',
      purposeCoordinationCare: false,
      purposeBilling: false,
      purposeEmergency: false,
      purposeSupportFromFamily: false,
      purposeOtherSpecify: '',
      durationOneYear: false,
      durationEndOfTreatment: false,
      durationSpecificDate: false,
      specificExpirationDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      isEAPClient: false,
      eapAuthorizeSharing: false,
      eapUnderstandNoDetails: false,
      eapEmployer: '',
      eapUnderstandRefusalImpact: false,
      allowClientEdits: true
    });
    setGeneratedLink('');
    setError('');
    setEmailTo('');
    setGreetingName('');
    setEmailMessage('');
    setEmailStatus('');
  };

  const handleSendROIEmail = async () => {
    if (!emailTo) return;
    setEmailSending(true);
    setEmailStatus('');
    try {
      await API.post('/therapist/send-roi-link', {
        recipientEmail: emailTo,
        roiLink: generatedLink,
        clientName: greetingName,
        message: emailMessage,
      });
      setEmailStatus('success');
    } catch (err) {
      console.error('Error sending ROI email:', err);
      setEmailStatus('error');
    } finally {
      setEmailSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={modalStyles.backdrop}>
      <div style={modalStyles.modal}>
        <div style={modalStyles.header}>
          <h3>Generate ROI Form for {clientName}</h3>
          <button style={modalStyles.closeButton} onClick={onClose}>×</button>
        </div>
        
        <div style={modalStyles.body}>
          {!generatedLink ? (
            <>
              <p>Pre-fill information for the client's Release of Information form:</p>
              
              {/* Quick Fill Options */}
              <div style={modalStyles.quickFillSection}>
                <h4>Quick Fill Options</h4>
                <div style={modalStyles.quickFillButtons}>
                  <button
                    type="button"
                    onClick={fillEAPData}
                    style={modalStyles.eapButton}
                  >
                    📋 EAP Client (Blue Dog Bakery)
                  </button>
                  <button
                    type="button"
                    onClick={fillPrimaryCareData}
                    style={modalStyles.primaryCareButton}
                  >
                    🏥 Primary Care Provider
                  </button>
                  <button
                    type="button"
                    onClick={fillSchoolData}
                    style={modalStyles.schoolButton}
                  >
                    🏫 School Coordination
                  </button>
                </div>
              </div>

              {/* Form Controls */}
              <div style={modalStyles.formControlsSection}>
                <h4>Form Settings</h4>
                <label style={modalStyles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={preFilledData.allowClientEdits}
                    onChange={(e) => handleInputChange('allowClientEdits', e.target.checked)}
                  />
                  Allow client to edit form fields
                  {!preFilledData.allowClientEdits && (
                    <span style={modalStyles.warningText}> (Form will be read-only for client)</span>
                  )}
                </label>
                <label style={modalStyles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={preFilledData.isEAPClient}
                    onChange={(e) => handleInputChange('isEAPClient', e.target.checked)}
                  />
                  This is an EAP client (shows EAP section)
                </label>
              </div>
              
              <div style={modalStyles.section}>
                <h4>Release Information To</h4>
                <div style={modalStyles.formGroup}>
                  <label>Name:</label>
                  <input
                    type="text"
                    value={preFilledData.authorizeReleaseTo}
                    onChange={(e) => handleInputChange('authorizeReleaseTo', e.target.value)}
                    style={modalStyles.input}
                    placeholder="Enter organization or individual name"
                  />
                </div>
                
                <div style={modalStyles.formRow}>
                  <div style={modalStyles.formGroup}>
                    <label>Relationship to client:</label>
                    <input
                      type="text"
                      value={preFilledData.releaseToRelationship}
                      onChange={(e) => handleInputChange('releaseToRelationship', e.target.value)}
                      style={modalStyles.input}
                      placeholder="e.g., Primary Care Provider, Employer"
                    />
                  </div>
                  
                  <div style={modalStyles.formGroup}>
                    <label>Phone/E-mail:</label>
                    <input
                      type="text"
                      value={preFilledData.releaseToPhone}
                      onChange={(e) => handleInputChange('releaseToPhone', e.target.value)}
                      style={modalStyles.input}
                      placeholder="Phone or email"
                    />
                  </div>
                </div>
              </div>
              
              <div style={modalStyles.section}>
                <h4>I authorize Avenues Unlimited to release or obtain the following information:</h4>
                <div style={modalStyles.checkboxGrid}>
                  {/* Action-based authorizations */}
                  <label style={modalStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={preFilledData.shareProgressWithProvider}
                      onChange={(e) => handleInputChange('shareProgressWithProvider', e.target.checked)}
                    />
                    Share progress and treatment details with my primary care provider or psychiatrist
                  </label>
                  
                  <label style={modalStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={preFilledData.coordinateCareWithPsychiatrist}
                      onChange={(e) => handleInputChange('coordinateCareWithPsychiatrist', e.target.checked)}
                    />
                    Coordinate care with my psychiatrist or prescribing provider
                  </label>
                  
                  <label style={modalStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={preFilledData.shareWithSchool}
                      onChange={(e) => handleInputChange('shareWithSchool', e.target.checked)}
                    />
                    Share treatment information with my child's school
                  </label>
                  
                  <label style={modalStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={preFilledData.speakWithFamily}
                      onChange={(e) => handleInputChange('speakWithFamily', e.target.checked)}
                    />
                    Speak with a family member, partner, or support person
                  </label>
                  
                  <label style={modalStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={preFilledData.billUsingOtherCard}
                      onChange={(e) => handleInputChange('billUsingOtherCard', e.target.checked)}
                    />
                    Bill services using a card not in my name
                  </label>
                  
                  <label style={modalStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={preFilledData.provideDocumentationToProvider}
                      onChange={(e) => handleInputChange('provideDocumentationToProvider', e.target.checked)}
                    />
                    Provide documentation to another mental health provider
                  </label>
                  
                  <label style={modalStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={preFilledData.allowEmergencyContactInform}
                      onChange={(e) => handleInputChange('allowEmergencyContactInform', e.target.checked)}
                    />
                    Allow emergency contacts to be informed in case of a crisis
                  </label>

                  {/* Information type authorizations */}
                  <label style={modalStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={preFilledData.schedulingInfoOnly}
                      onChange={(e) => handleInputChange('schedulingInfoOnly', e.target.checked)}
                    />
                    Scheduling or appointment information only
                  </label>
                  
                  <label style={modalStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={preFilledData.treatmentProgress}
                      onChange={(e) => handleInputChange('treatmentProgress', e.target.checked)}
                    />
                    Treatment progress and participation
                  </label>
                  
                  <label style={modalStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={preFilledData.diagnosisAndPlan}
                      onChange={(e) => handleInputChange('diagnosisAndPlan', e.target.checked)}
                    />
                    Diagnosis and treatment plan
                  </label>
                  
                  <label style={modalStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={preFilledData.billingAndPayment}
                      onChange={(e) => handleInputChange('billingAndPayment', e.target.checked)}
                    />
                    Billing and payment details
                  </label>
                  
                  <label style={modalStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={preFilledData.fullClinicalRecords}
                      onChange={(e) => handleInputChange('fullClinicalRecords', e.target.checked)}
                    />
                    Full clinical records
                  </label>
                </div>
                
                <div style={modalStyles.formGroup}>
                  <label>Other:</label>
                  <input
                    type="text"
                    value={preFilledData.releaseOtherSpecify}
                    onChange={(e) => handleInputChange('releaseOtherSpecify', e.target.value)}
                    style={modalStyles.input}
                    placeholder="Specify other information to release"
                  />
                </div>
              </div>
              
              <div style={modalStyles.section}>
                <h4>The purpose of disclosure:</h4>
                <div style={modalStyles.checkboxGrid}>
                  <label style={modalStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={preFilledData.purposeCoordinationCare}
                      onChange={(e) => handleInputChange('purposeCoordinationCare', e.target.checked)}
                    />
                    Coordination of care
                  </label>
                  
                  <label style={modalStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={preFilledData.purposeBilling}
                      onChange={(e) => handleInputChange('purposeBilling', e.target.checked)}
                    />
                    Billing
                  </label>
                  
                  <label style={modalStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={preFilledData.purposeEmergency}
                      onChange={(e) => handleInputChange('purposeEmergency', e.target.checked)}
                    />
                    Emergency or safety planning
                  </label>
                  
                  <label style={modalStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={preFilledData.purposeSupportFromFamily}
                      onChange={(e) => handleInputChange('purposeSupportFromFamily', e.target.checked)}
                    />
                    Support from family/friends
                  </label>
                </div>
                
                <div style={modalStyles.formGroup}>
                  <label>Other:</label>
                  <input
                    type="text"
                    value={preFilledData.purposeOtherSpecify}
                    onChange={(e) => handleInputChange('purposeOtherSpecify', e.target.value)}
                    style={modalStyles.input}
                    placeholder="Specify other purpose"
                  />
                </div>
              </div>
              
              <div style={modalStyles.section}>
                <h4>This release will remain in effect until (choose one):</h4>
                <div style={modalStyles.checkboxGrid}>
                  <label style={modalStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={preFilledData.durationOneYear}
                      onChange={(e) => handleInputChange('durationOneYear', e.target.checked)}
                    />
                    One year from today
                  </label>
                  
                  <label style={modalStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={preFilledData.durationEndOfTreatment}
                      onChange={(e) => handleInputChange('durationEndOfTreatment', e.target.checked)}
                    />
                    End of treatment
                  </label>
                  
                  <label style={modalStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={preFilledData.durationSpecificDate}
                      onChange={(e) => handleInputChange('durationSpecificDate', e.target.checked)}
                    />
                    Specific date:
                  </label>
                </div>
                
                {preFilledData.durationSpecificDate && (
                  <div style={modalStyles.formGroup}>
                    <label>Expiration Date:</label>
                    <input
                      type="date"
                      value={preFilledData.specificExpirationDate}
                      onChange={(e) => handleInputChange('specificExpirationDate', e.target.value)}
                      style={modalStyles.input}
                    />
                  </div>
                )}
              </div>

              {/* EAP Section - Only show if isEAPClient is checked */}
              {preFilledData.isEAPClient && (
                <div style={modalStyles.section}>
                  <h4>Employee Assistance Program (EAP) Services:</h4>
                  <div style={modalStyles.formGroup}>
                    <label>My employer is:</label>
                    <input
                      type="text"
                      value={preFilledData.eapEmployer}
                      onChange={(e) => handleInputChange('eapEmployer', e.target.value)}
                      style={modalStyles.input}
                      placeholder="Enter employer name"
                    />
                  </div>
                  
                  <div style={modalStyles.checkboxGrid}>
                    <label style={modalStyles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={preFilledData.eapAuthorizeSharing}
                        onChange={(e) => handleInputChange('eapAuthorizeSharing', e.target.checked)}
                      />
                      I authorize Avenues Unlimited to share session dates and information necessary for billing with my employer for the purpose of billing for services.
                    </label>
                    
                    <label style={modalStyles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={preFilledData.eapUnderstandNoDetails}
                        onChange={(e) => handleInputChange('eapUnderstandNoDetails', e.target.checked)}
                      />
                      I understand that no clinical details will be shared unless explicitly authorized.
                    </label>
                    
                    <label style={modalStyles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={preFilledData.eapUnderstandRefusalImpact}
                        onChange={(e) => handleInputChange('eapUnderstandRefusalImpact', e.target.checked)}
                      />
                      I understand that refusing this release may impact my ability to use EAP services at this practice.
                    </label>
                  </div>
                </div>
              )}

              {error && <div style={modalStyles.error}>{error}</div>}
              
              <div style={modalStyles.actions}>
                <button
                  style={modalStyles.resetButton}
                  onClick={resetForm}
                >
                  Reset Form
                </button>
                <button
                  style={modalStyles.generateButton}
                  onClick={generateROILink}
                  disabled={loading}
                >
                  {loading ? 'Generating...' : 'Generate ROI Link'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ padding: '8px 0' }}>
                <h4 style={{ color: '#1D2C5F', margin: '0 0 4px 0' }}>✓ ROI Form Link Generated</h4>
                <p style={{ margin: '0 0 12px 0', fontSize: 13, color: '#555' }}>
                  Send the form link to {clientFullName} via email, or copy the link to send it yourself.
                  {!preFilledData.allowClientEdits && (
                    <span style={{ color: '#c0392b' }}> Form is set to read-only for client.</span>
                  )}
                </p>

                {/* Generated link + copy button */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
                  <input
                    type="text"
                    readOnly
                    value={generatedLink}
                    onFocus={e => e.target.select()}
                    style={{ flex: 1, minWidth: 0, padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13, background: '#f7f7f7', color: '#333', boxSizing: 'border-box' }}
                  />
                  <button
                    type="button"
                    onClick={copyToClipboard}
                    style={{ ...modalStyles.resetButton, whiteSpace: 'nowrap', background: linkCopied ? '#1D2C5F' : undefined, color: linkCopied ? '#fff' : undefined }}
                  >
                    {linkCopied ? '✓ Copied!' : 'Copy Link'}
                  </button>
                </div>

                {emailStatus === 'success' ? (
                  <div style={{ textAlign: 'center', padding: '24px 0' }}>
                    <div style={{ fontSize: 40 }}>✅</div>
                    <p style={{ color: '#1D2C5F', fontWeight: 600, marginTop: 8 }}>ROI form link sent successfully!</p>
                    <button
                      style={{ ...modalStyles.generateButton, marginTop: 16 }}
                      onClick={() => { resetForm(); onClose(); }}
                    >
                      Done
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                    {/* Compose panel */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4 }}>To</label>
                        <input
                          type="email"
                          value={emailTo}
                          onChange={e => setEmailTo(e.target.value)}
                          placeholder="client@email.com"
                          style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                        />
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Recipient name (greeting)</label>
                        <input
                          type="text"
                          value={greetingName}
                          onChange={e => setGreetingName(e.target.value)}
                          placeholder="First Last"
                          style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                        />
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Message</label>
                        <textarea
                          value={emailMessage}
                          onChange={e => setEmailMessage(e.target.value)}
                          rows={5}
                          placeholder="Message to the client..."
                          style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
                        />
                      </div>
                      {emailStatus === 'error' && (
                        <p style={{ color: '#c0392b', fontSize: 13, margin: '0 0 8px 0' }}>Failed to send. Please try again.</p>
                      )}
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button
                          style={{ ...modalStyles.generateButton, opacity: emailSending || !emailTo ? 0.6 : 1 }}
                          onClick={handleSendROIEmail}
                          disabled={emailSending || !emailTo}
                        >
                          {emailSending ? 'Sending…' : 'Send Email'}
                        </button>
                        <button
                          style={{ ...modalStyles.resetButton }}
                          onClick={() => { resetForm(); onClose(); }}
                        >
                          Skip
                        </button>
                      </div>
                    </div>

                    {/* Preview panel */}
                    <div style={{ flex: 1, minWidth: 0, border: '1px solid #ddd', borderRadius: 8, overflow: 'hidden', fontSize: 12 }}>
                      <div style={{ background: '#1D2C5F', padding: '14px 16px', textAlign: 'center' }}>
                        <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>Avenues Unlimited Counseling Center</span>
                      </div>
                      <div style={{ padding: '16px', background: '#fff' }}>
                        <p style={{ margin: '0 0 10px 0', fontSize: 13 }}>Hello{greetingName ? ` ${greetingName}` : ''},</p>
                        {emailMessage
                          ? <p style={{ margin: '0 0 10px 0', fontSize: 13, whiteSpace: 'pre-wrap' }}>{emailMessage}</p>
                          : <p style={{ margin: '0 0 10px 0', fontSize: 13, color: '#888' }}>{DEFAULT_ROI_EMAIL_MESSAGE}</p>
                        }
                        <div style={{ textAlign: 'center', margin: '16px 0' }}>
                          <span style={{ background: '#1D2C5F', color: '#fff', padding: '10px 22px', borderRadius: 6, fontSize: 13, display: 'inline-block' }}>
                            Complete Your ROI Form →
                          </span>
                        </div>
                        <p style={{ fontSize: 11, color: '#888', margin: 0 }}>This link will expire in 7 days.</p>
                      </div>
                      <div style={{ background: '#f5f5f5', padding: '10px', textAlign: 'center', fontSize: 10, color: '#888' }}>
                        Avenues Unlimited Counseling Center &bull; 1774 Mellwood Ave, Louisville, KY &bull; 502.459.9635
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Modal for viewing completed ROI submissions - Updated with combined authorization section
export function ViewROISubmissionModal({ isOpen, onClose, submission, onSendAsPDF }) {
  const [showPrintPreview, setShowPrintPreview] = useState(false);

  if (!isOpen || !submission) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not specified';
    const [year, month, day] = dateStr.split('-');
    return `${month}/${day}/${year}`;
  };

  // Helper function to check both camelCase and snake_case field names
  const getFieldValue = (obj, camelCase, snakeCase) => {
    return obj[camelCase] || obj[snakeCase] || false;
  };

  const getFieldString = (obj, camelCase, snakeCase) => {
    return obj[camelCase] || obj[snakeCase] || '';
  };

  const formatBoolean = (value) => value ? '✓' : '';

  // Print function matching client's print format
  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=800,height=900');
    if (!printWindow) {
      alert('Failed to open new window for printing. Please check your popup blocker settings.');
      return;
    }

    const printContent = generatePrintContent(submission);
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Print after content loads
    printWindow.onload = function() {
      printWindow.focus();
      printWindow.print();
    };
  };

  // Generate print content matching the client's review page format
  const generatePrintContent = (data) => {
    const today = formatDate(todayEastern());
    
    return `
      <html>
      <head>
        <title>Authorization for Release of Information</title>
        <style>
          body {
            font-family: 'Times New Roman', Times, serif;
            margin: 20px;
            padding: 20px;
            line-height: 1.5;
            font-size: 12px;
            color: #333;
          }
          .page {
            max-width: 800px;
            margin: 0 auto;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
          }
          .header img {
            max-width: 100%;
            height: auto;
          }
          .footer {
            text-align: center;
            margin-top: 40px;
            margin-bottom: 20px;
          }
          .footer img {
            max-width: 100%;
            height: auto;
          }
          .form-title {
            text-align: center;
            color: #2c3e50;
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 30px;
            padding: 15px;
            border: 2px solid #2c3e50;
            background: #f8f9fa;
          }
          .review-document {
            border: 2px solid #2c3e50;
            padding: 30px;
            margin: 20px 0;
            background: #fff;
          }
          .review-section {
            margin-bottom: 25px;
            padding-bottom: 15px;
            border-bottom: 1px solid #eee;
          }
          .review-section:last-child {
            border-bottom: none;
          }
          .review-section h3 {
            color: #2c3e50;
            font-size: 14px;
            font-weight: bold;
            margin-bottom: 10px;
            border-bottom: 1px solid #ddd;
            padding-bottom: 5px;
          }
          .review-section p {
            margin: 8px 0;
            line-height: 1.4;
            font-size: 12px;
          }
          .review-checkboxes p {
            margin: 5px 0;
            padding-left: 10px;
          }
          .signature-section {
            margin: 20px 0;
            border: 2px solid #2c3e50;
            padding: 15px;
            background: #f8f9fa;
          }
          .signature-img {
            max-width: 300px;
            max-height: 80px;
            border: 1px solid #333;
            margin: 5px 0;
            background: white;
          }
        </style>
      </head>
      <body>
        <div class="page">
          <div class="header">
            <img src="https://www.avenuesportal.com/letterhead.png" alt="Avenues Unlimited">
          </div>
          
          <div class="form-title">AUTHORIZATION FOR RELEASE OF INFORMATION</div>
          
          <div class="review-document">
            <div class="review-section">
              <h3>Client Information</h3>
              <p><strong>Name:</strong> ${getFieldString(data, 'clientName', 'client_name')}</p>
              <p><strong>Date of Birth:</strong> ${formatDate(getFieldString(data, 'clientDob', 'client_dob'))}</p>
              <p><strong>Phone:</strong> ${getFieldString(data, 'clientPhone', 'client_phone')}</p>
              <p><strong>Address:</strong> ${getFieldString(data, 'clientAddress', 'client_address')}</p>
            </div>
            
            <div class="review-section">
              <h3>Information Release Authorization</h3>
              <p><strong>Release information to:</strong> ${getFieldString(data, 'authorizeReleaseTo', 'authorize_release_to')}</p>
              <p><strong>Relationship:</strong> ${getFieldString(data, 'releaseToRelationship', 'release_to_relationship')}</p>
              <p><strong>Contact:</strong> ${getFieldString(data, 'releaseToPhone', 'release_to_phone')}</p>
            </div>
            
            <div class="review-section">
              <h3>Authorized Information Release</h3>
              <div class="review-checkboxes">
                ${getFieldValue(data, 'shareProgressWithProvider', 'share_progress_with_provider') ? '<p>✓ Share progress and treatment details with my primary care provider or psychiatrist</p>' : ''}
                ${getFieldValue(data, 'coordinateCareWithPsychiatrist', 'coordinate_care_with_psychiatrist') ? '<p>✓ Coordinate care with my psychiatrist or prescribing provider</p>' : ''}
                ${getFieldValue(data, 'shareWithSchool', 'share_with_school') ? '<p>✓ Share treatment information with my child\'s school</p>' : ''}
                ${getFieldValue(data, 'speakWithFamily', 'speak_with_family') ? '<p>✓ Speak with a family member, partner, or support person</p>' : ''}
                ${getFieldValue(data, 'billUsingOtherCard', 'bill_using_other_card') ? '<p>✓ Bill services using a card not in my name</p>' : ''}
                ${getFieldValue(data, 'provideDocumentationToProvider', 'provide_documentation_to_provider') ? '<p>✓ Provide documentation to another mental health provider</p>' : ''}
                ${getFieldValue(data, 'allowEmergencyContactInform', 'allow_emergency_contact_inform') ? '<p>✓ Allow emergency contacts to be informed in case of a crisis</p>' : ''}
                ${getFieldValue(data, 'schedulingInfoOnly', 'scheduling_info_only') ? '<p>✓ Scheduling or appointment information only</p>' : ''}
                ${getFieldValue(data, 'treatmentProgress', 'treatment_progress') ? '<p>✓ Treatment progress and participation</p>' : ''}
                ${getFieldValue(data, 'diagnosisAndPlan', 'diagnosis_and_plan') ? '<p>✓ Diagnosis and treatment plan</p>' : ''}
                ${getFieldValue(data, 'billingAndPayment', 'billing_and_payment') ? '<p>✓ Billing and payment details</p>' : ''}
                ${getFieldValue(data, 'fullClinicalRecords', 'full_clinical_records') ? '<p>✓ Full clinical records</p>' : ''}
                ${getFieldString(data, 'releaseOtherSpecify', 'release_other_specify') ? `<p>✓ Other: ${getFieldString(data, 'releaseOtherSpecify', 'release_other_specify')}</p>` : ''}
              </div>
            </div>
            
            <div class="review-section">
              <h3>Purpose of Disclosure</h3>
              <div class="review-checkboxes">
                ${getFieldValue(data, 'purposeCoordinationCare', 'purpose_coordination_care') ? '<p>✓ Coordination of care</p>' : ''}
                ${getFieldValue(data, 'purposeBilling', 'purpose_billing') ? '<p>✓ Billing</p>' : ''}
                ${getFieldValue(data, 'purposeEmergency', 'purpose_emergency') ? '<p>✓ Emergency or safety planning</p>' : ''}
                ${getFieldValue(data, 'purposeSupportFromFamily', 'purpose_support_from_family') ? '<p>✓ Support from family/friends</p>' : ''}
                ${getFieldString(data, 'purposeOtherSpecify', 'purpose_other_specify') ? `<p>✓ Other: ${getFieldString(data, 'purposeOtherSpecify', 'purpose_other_specify')}</p>` : ''}
              </div>
            </div>
            
            <div class="review-section">
              <h3>Duration</h3>
              <p>
                ${getFieldValue(data, 'durationOneYear', 'duration_one_year') ? 'One year from today' : ''}
                ${getFieldValue(data, 'durationEndOfTreatment', 'duration_end_of_treatment') ? 'End of treatment' : ''}
                ${getFieldValue(data, 'durationSpecificDate', 'duration_specific_date') ? `Until: ${formatDate(getFieldString(data, 'specificExpirationDate', 'specific_expiration_date'))}` : ''}
              </p>
            </div>
            
            ${(getFieldValue(data, 'eapAuthorizeSharing', 'eap_authorize_sharing') || getFieldString(data, 'eapEmployer', 'eap_employer')) ? `
            <div class="review-section">
              <h3>Employee Assistance Program (EAP)</h3>
              ${getFieldString(data, 'eapEmployer', 'eap_employer') ? `<p><strong>Employer:</strong> ${getFieldString(data, 'eapEmployer', 'eap_employer')}</p>` : ''}
              <div class="review-checkboxes">
                ${getFieldValue(data, 'eapAuthorizeSharing', 'eap_authorize_sharing') ? '<p>✓ Authorized to share session dates and billing information with employer</p>' : ''}
                ${getFieldValue(data, 'eapUnderstandNoDetails', 'eap_understand_no_details') ? '<p>✓ Understands no clinical details will be shared unless explicitly authorized</p>' : ''}
                ${getFieldValue(data, 'eapUnderstandRefusalImpact', 'eap_understand_refusal_impact') ? '<p>✓ Understands refusal may impact ability to use EAP services</p>' : ''}
              </div>
            </div>
            ` : ''}
            
            <div class="review-section">
              <h3>Signatures</h3>
              ${getFieldString(data, 'clientSignatureData', 'client_signature_data') ? `
                <div>
                  <p><strong>Client Signature:</strong></p>
                  <img src="${getFieldString(data, 'clientSignatureData', 'client_signature_data')}" class="signature-img" alt="Client Signature">
                  <p><strong>Date:</strong> ${formatDate(getFieldString(data, 'clientSignatureDate', 'client_signature_date'))}</p>
                </div>
              ` : '<p><strong>Client Signature:</strong> Not provided</p>'}
              
              ${getFieldString(data, 'guardianSignatureData', 'guardian_signature_data') ? `
                <div style="margin-top: 15px;">
                  <p><strong>Guardian Signature:</strong></p>
                  <img src="${getFieldString(data, 'guardianSignatureData', 'guardian_signature_data')}" class="signature-img" alt="Guardian Signature">
                  <p><strong>Relationship:</strong> ${getFieldString(data, 'guardianRelationship', 'guardian_relationship')}</p>
                  <p><strong>Date:</strong> ${formatDate(getFieldString(data, 'guardianSignatureDate', 'guardian_signature_date'))}</p>
                </div>
              ` : ''}
              
              ${getFieldString(data, 'witnessSignatureData', 'witness_signature_data') ? `
                <div style="margin-top: 15px;">
                  <p><strong>Witness:</strong> ${getFieldString(data, 'witnessName', 'witness_name')}</p>
                  <img src="${getFieldString(data, 'witnessSignatureData', 'witness_signature_data')}" class="signature-img" alt="Witness Signature">
                  <p><strong>Date:</strong> ${formatDate(getFieldString(data, 'witnessSignatureDate', 'witness_signature_date'))}</p>
                </div>
              ` : ''}
            </div>
          </div>
          
          <div class="footer">
            <img src="https://www.avenuesportal.com/footer.png" alt="Avenues Footer">
          </div>
        </div>
        
        <script>
          window.onload = function() {
            window.focus();
            window.print();
          };
        </script>
      </body>
      </html>
    `;
  };

  return (
    <div style={modalStyles.backdrop}>
      <div style={{...modalStyles.modal, width: '900px', maxHeight: '90vh'}}>
        <div style={modalStyles.header}>
          <h3>Release of Information - {getFieldString(submission, 'clientName', 'client_name')}</h3>
          <button style={modalStyles.closeButton} onClick={onClose}>×</button>
        </div>
        
        <div style={{...modalStyles.body, maxHeight: '70vh', overflowY: 'auto'}}>
          <div style={modalStyles.section}>
            <h4>Client Information</h4>
            <div style={modalStyles.infoGrid}>
              <div><strong>Name:</strong> {getFieldString(submission, 'clientName', 'client_name')}</div>
              <div><strong>Date of Birth:</strong> {formatDate(getFieldString(submission, 'clientDob', 'client_dob'))}</div>
              <div><strong>Address:</strong> {getFieldString(submission, 'clientAddress', 'client_address')}</div>
              <div><strong>Phone:</strong> {getFieldString(submission, 'clientPhone', 'client_phone')}</div>
            </div>
          </div>
          
          <div style={modalStyles.section}>
            <h4>Release Information To</h4>
            <div style={modalStyles.infoGrid}>
              <div><strong>Name:</strong> {getFieldString(submission, 'authorizeReleaseTo', 'authorize_release_to')}</div>
              <div><strong>Relationship:</strong> {getFieldString(submission, 'releaseToRelationship', 'release_to_relationship')}</div>
              <div><strong>Phone/Email:</strong> {getFieldString(submission, 'releaseToPhone', 'release_to_phone')}</div>
            </div>
          </div>
          
          <div style={modalStyles.section}>
            <h4>Authorized Information Release</h4>
            <div style={modalStyles.checkboxResults}>
              <div>{formatBoolean(getFieldValue(submission, 'shareProgressWithProvider', 'share_progress_with_provider'))} Share progress and treatment details with my primary care provider or psychiatrist</div>
              <div>{formatBoolean(getFieldValue(submission, 'coordinateCareWithPsychiatrist', 'coordinate_care_with_psychiatrist'))} Coordinate care with my psychiatrist or prescribing provider</div>
              <div>{formatBoolean(getFieldValue(submission, 'shareWithSchool', 'share_with_school'))} Share treatment information with my child's school</div>
              <div>{formatBoolean(getFieldValue(submission, 'speakWithFamily', 'speak_with_family'))} Speak with a family member, partner, or support person</div>
              <div>{formatBoolean(getFieldValue(submission, 'billUsingOtherCard', 'bill_using_other_card'))} Bill services using a card not in my name</div>
              <div>{formatBoolean(getFieldValue(submission, 'provideDocumentationToProvider', 'provide_documentation_to_provider'))} Provide documentation to another mental health provider</div>
              <div>{formatBoolean(getFieldValue(submission, 'allowEmergencyContactInform', 'allow_emergency_contact_inform'))} Allow emergency contacts to be informed in case of a crisis</div>
              <div>{formatBoolean(getFieldValue(submission, 'schedulingInfoOnly', 'scheduling_info_only'))} Scheduling or appointment information only</div>
              <div>{formatBoolean(getFieldValue(submission, 'treatmentProgress', 'treatment_progress'))} Treatment progress and participation</div>
              <div>{formatBoolean(getFieldValue(submission, 'diagnosisAndPlan', 'diagnosis_and_plan'))} Diagnosis and treatment plan</div>
              <div>{formatBoolean(getFieldValue(submission, 'billingAndPayment', 'billing_and_payment'))} Billing and payment details</div>
              <div>{formatBoolean(getFieldValue(submission, 'fullClinicalRecords', 'full_clinical_records'))} Full clinical records</div>
              {getFieldString(submission, 'releaseOtherSpecify', 'release_other_specify') && (
                <div>✓ Other: {getFieldString(submission, 'releaseOtherSpecify', 'release_other_specify')}</div>
              )}
            </div>
          </div>
          
          <div style={modalStyles.section}>
            <h4>Purpose of Disclosure</h4>
            <div style={modalStyles.checkboxResults}>
              <div>{formatBoolean(getFieldValue(submission, 'purposeCoordinationCare', 'purpose_coordination_care'))} Coordination of care</div>
              <div>{formatBoolean(getFieldValue(submission, 'purposeBilling', 'purpose_billing'))} Billing</div>
              <div>{formatBoolean(getFieldValue(submission, 'purposeEmergency', 'purpose_emergency'))} Emergency or safety planning</div>
              <div>{formatBoolean(getFieldValue(submission, 'purposeSupportFromFamily', 'purpose_support_from_family'))} Support from family/friends</div>
              {getFieldString(submission, 'purposeOtherSpecify', 'purpose_other_specify') && (
                <div>✓ Other: {getFieldString(submission, 'purposeOtherSpecify', 'purpose_other_specify')}</div>
              )}
            </div>
          </div>
          
          <div style={modalStyles.section}>
            <h4>Duration</h4>
            <div style={modalStyles.infoGrid}>
              <div><strong>Duration:</strong> 
                {getFieldValue(submission, 'durationOneYear', 'duration_one_year') && 'One year from today'}
                {getFieldValue(submission, 'durationEndOfTreatment', 'duration_end_of_treatment') && 'End of treatment'}
                {getFieldValue(submission, 'durationSpecificDate', 'duration_specific_date') && `Until: ${formatDate(getFieldString(submission, 'specificExpirationDate', 'specific_expiration_date'))}`}
              </div>
            </div>
          </div>
          
          {(getFieldValue(submission, 'eapAuthorizeSharing', 'eap_authorize_sharing') || getFieldString(submission, 'eapEmployer', 'eap_employer')) && (
            <div style={modalStyles.section}>
              <h4>Employee Assistance Program (EAP)</h4>
              {getFieldString(submission, 'eapEmployer', 'eap_employer') && <div><strong>Employer:</strong> {getFieldString(submission, 'eapEmployer', 'eap_employer')}</div>}
              <div style={modalStyles.checkboxResults}>
                <div>{formatBoolean(getFieldValue(submission, 'eapAuthorizeSharing', 'eap_authorize_sharing'))} Authorized to share session dates and billing information with employer</div>
                <div>{formatBoolean(getFieldValue(submission, 'eapUnderstandNoDetails', 'eap_understand_no_details'))} Understands no clinical details will be shared unless explicitly authorized</div>
                <div>{formatBoolean(getFieldValue(submission, 'eapUnderstandRefusalImpact', 'eap_understand_refusal_impact'))} Understands refusal may impact ability to use EAP services</div>
              </div>
            </div>
          )}
          
          <div style={modalStyles.section}>
            <h4>Authorization Details</h4>
            <div style={modalStyles.infoGrid}>
              <div><strong>Submitted:</strong> {formatDate(submission.created_at)}</div>
              <div><strong>Client Signed:</strong> {formatDate(getFieldString(submission, 'clientSignatureDate', 'client_signature_date'))}</div>
              {getFieldString(submission, 'guardianName', 'guardian_name') && (
                <>
                  <div><strong>Guardian:</strong> {getFieldString(submission, 'guardianName', 'guardian_name')}</div>
                  <div><strong>Guardian Signed:</strong> {formatDate(getFieldString(submission, 'guardianSignatureDate', 'guardian_signature_date'))}</div>
                </>
              )}
              {getFieldString(submission, 'witnessName', 'witness_name') && (
                <>
                  <div><strong>Witness:</strong> {getFieldString(submission, 'witnessName', 'witness_name')}</div>
                  <div><strong>Witness Signed:</strong> {formatDate(getFieldString(submission, 'witnessSignatureDate', 'witness_signature_date'))}</div>
                </>
              )}
            </div>
          </div>
          
          <div style={modalStyles.section}>
            <h4>Signatures</h4>
            <div style={modalStyles.signatureGrid}>
              {getFieldString(submission, 'clientSignatureData', 'client_signature_data') && (
                <div style={modalStyles.signatureItem}>
                  <strong>Client Signature:</strong>
                  <img 
                    src={getFieldString(submission, 'clientSignatureData', 'client_signature_data')} 
                    alt="Client Signature" 
                    style={modalStyles.signatureImage}
                  />
                </div>
              )}
              
              {getFieldString(submission, 'guardianSignatureData', 'guardian_signature_data') && (
                <div style={modalStyles.signatureItem}>
                  <strong>Guardian Signature:</strong>
                  <img 
                    src={getFieldString(submission, 'guardianSignatureData', 'guardian_signature_data')} 
                    alt="Guardian Signature" 
                    style={modalStyles.signatureImage}
                  />
                </div>
              )}
              
              {getFieldString(submission, 'witnessSignatureData', 'witness_signature_data') && (
                <div style={modalStyles.signatureItem}>
                  <strong>Witness Signature:</strong>
                  <img 
                    src={getFieldString(submission, 'witnessSignatureData', 'witness_signature_data')} 
                    alt="Witness Signature" 
                    style={modalStyles.signatureImage}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div style={modalStyles.footer}>
          <button style={modalStyles.printButton} onClick={handlePrint}>
            🖨️ Print Form
          </button>
          {onSendAsPDF && (
            <button
              style={{ ...modalStyles.printButton, backgroundColor: "#1D2C5F", marginLeft: "8px" }}
              onClick={() => onSendAsPDF(submission)}
            >
              ✉️ Send as PDF
            </button>
          )}
          <button style={modalStyles.closeModalButton} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Component to list ROI submissions for a client
export function ROISubmissionsList({ clientId, onViewSubmission }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (clientId) {
      fetchSubmissions();
    }
  }, [clientId]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const response = await API.get(`/forms/therapist/roi-submissions/${clientId}`);
      setSubmissions(response.data);
    } catch (err) {
      console.error('Error fetching ROI submissions:', err);
      setError('Error loading ROI submissions');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div>Loading ROI submissions...</div>;
  if (error) return <div style={{color: 'red'}}>{error}</div>;

  return (
    <div style={modalStyles.section}>
      <h4>Release of Information Forms</h4>
      {submissions.length === 0 ? (
        <p style={{fontStyle: 'italic', color: '#666'}}>No ROI forms submitted yet.</p>
      ) : (
        <div style={modalStyles.submissionsList}>
          {submissions.map((submission) => (
            <div
              key={submission.id}
              style={modalStyles.submissionItem}
              onClick={() => onViewSubmission(submission)}
            >
              <div style={modalStyles.submissionHeader}>
                <strong>Release to: {submission.authorize_release_to || submission.authorizeReleaseTo}</strong>
                <span style={modalStyles.submissionDate}>
                  {new Date(submission.created_at).toLocaleDateString()}
                </span>
              </div>
              <div style={modalStyles.submissionMeta}>
                Expires: {new Date(submission.specific_expiration_date || submission.specificExpirationDate || new Date()).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Enhanced styles with new elements
const modalStyles = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  modal: {
    width: '900px',
    maxWidth: '95vw',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #eee'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#999'
  },
  body: {
    flex: '1',
    padding: '20px',
    overflowY: 'auto'
  },
  footer: {
    padding: '15px 20px',
    borderTop: '1px solid #eee',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px'
  },
  section: {
    marginBottom: '25px'
  },
  quickFillSection: {
    backgroundColor: '#f0f8ff',
    border: '1px solid #b0d4f1',
    borderRadius: '6px',
    padding: '15px',
    marginBottom: '20px'
  },
  quickFillButtons: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap'
  },
  eapButton: {
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    padding: '10px 15px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '5px'
  },
  primaryCareButton: {
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    padding: '10px 15px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '5px'
  },
  schoolButton: {
    backgroundColor: '#FF9800',
    color: 'white',
    border: 'none',
    padding: '10px 15px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '5px'
  },
  formControlsSection: {
    backgroundColor: '#fff9e6',
    border: '1px solid #ffd700',
    borderRadius: '6px',
    padding: '15px',
    marginBottom: '20px'
  },
  warningText: {
    color: '#d63384',
    fontSize: '12px',
    fontStyle: 'italic'
  },
  formGroup: {
    marginBottom: '15px'
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '15px'
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    resize: 'vertical',
    boxSizing: 'border-box'
  },
  checkboxGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '10px'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    backgroundColor: '#f8f9fa'
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '20px'
  },
  generateButton: {
    backgroundColor: '#1D2C5F',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  resetButton: {
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  copyButton: {
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  copiedButton: {
    backgroundColor: '#20c997',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  printButton: {
    backgroundColor: '#17a2b8',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  closeModalButton: {
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  error: {
    color: '#dc3545',
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '4px',
    padding: '10px',
    marginBottom: '15px'
  },
  successSection: {
    textAlign: 'center'
  },
  linkContainer: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    padding: '15px',
    margin: '15px 0',
    wordBreak: 'break-all'
  },
  expiryNote: {
    fontSize: '14px',
    color: '#6c757d',
    fontStyle: 'italic'
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '10px',
    fontSize: '14px'
  },
  checkboxResults: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '5px',
    fontSize: '14px'
  },
  signatureGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  signatureItem: {
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    padding: '10px'
  },
  signatureImage: {
    maxWidth: '300px',
    maxHeight: '100px',
    border: '1px solid #ccc',
    marginTop: '5px'
  },
  submissionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  submissionItem: {
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    padding: '15px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#f8f9fa'
    }
  },
  submissionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '5px'
  },
  submissionDate: {
    fontSize: '14px',
    color: '#6c757d'
  },
  submissionMeta: {
    fontSize: '13px',
    color: '#6c757d'
  }
};